// Tilt source for parallax: device orientation (γ/28, (β−50)/28) when the
// phone supports it, pointer position on the frame otherwise. Values are
// targets in [-1, 1]; the scroll engine eases them each frame (×.06) and
// writes --tx / --ty on the frame. Port of prototype lines 484–491.
import { useEffect, useRef } from 'react';

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

export default function useTilt(frameRef) {
  const tilt = useRef({ x: 0, y: 0 }); // eased (owned by the engine)
  const tiltT = useRef({ x: 0, y: 0 }); // target
  const ptr = useRef(null); // { x, y, t } last pointer position inside the frame (frame space)
  const gyro = useRef(false);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;

    const onMove = (e) => {
      const a = frame.getBoundingClientRect();
      const x = e.clientX - a.left, y = e.clientY - a.top;
      ptr.current = { x, y, t: performance.now() };
      if (!gyro.current) {
        tiltT.current.x = clamp((x / a.width - 0.5) * 2, -1, 1);
        tiltT.current.y = clamp((y / a.height - 0.5) * 2, -1, 1);
      }
    };
    const onDown = (e) => {
      const a = frame.getBoundingClientRect();
      ptr.current = { x: e.clientX - a.left, y: e.clientY - a.top, t: performance.now() };
    };
    const onLeave = () => { ptr.current = null; };
    const onOri = (e) => {
      if (e.gamma == null || e.beta == null) return;
      gyro.current = true;
      tiltT.current.x = clamp(e.gamma / 28, -1, 1);
      tiltT.current.y = clamp((e.beta - 50) / 28, -1, 1);
    };
    // iOS: orientation events need a user-gesture permission request.
    const onTouch = () => {
      try {
        if (typeof DeviceOrientationEvent !== 'undefined' && DeviceOrientationEvent.requestPermission) {
          DeviceOrientationEvent.requestPermission().catch(() => {});
        }
      } catch (_) { /* denial is silent */ }
      frame.removeEventListener('touchend', onTouch);
    };

    frame.addEventListener('pointermove', onMove, { passive: true });
    frame.addEventListener('pointerdown', onDown, { passive: true });
    frame.addEventListener('pointerleave', onLeave);
    frame.addEventListener('touchend', onTouch, { passive: true });
    window.addEventListener('deviceorientation', onOri);
    return () => {
      frame.removeEventListener('pointermove', onMove);
      frame.removeEventListener('pointerdown', onDown);
      frame.removeEventListener('pointerleave', onLeave);
      frame.removeEventListener('touchend', onTouch);
      window.removeEventListener('deviceorientation', onOri);
    };
  }, [frameRef]);

  return { tilt, tiltT, ptr };
}
